import moment from "moment";
import { IChase } from "../../types/chase-type";
import { BRANDS, BrandKey } from "../../utils/new-templates/brands";
import { IPT_RATE, r2 } from "../../utils/create-chase.constants";

/** What GET /pub/chase/:policyRef returns — the public slice of a chase. */
export type PublicChase = Pick<
  IChase,
  | "policyReferenceNumber"
  | "versionReferenceNumber"
  | "forename"
  | "surname"
  | "companyName"
  | "renewalDate"
  | "status"
  | "brandCode"
  | "coverType"
  | "insurer"
  | "policyNumber"
  | "policyType"
  | "paymentType"
  | "premium"
  | "totalPayable"
  | "monthlyAmount"
  | "instalments"
  | "interestRate"
  | "quote"
>;

export type Row = {
  label: string;
  value: string;
  /** Charged on the line above — rendered indented, under an elbow. */
  sub?: boolean;
  /** Money coming off the bill, not on to it. */
  credit?: boolean;
};

/** Money is in — nothing left to pay, whether or not an agent has renewed it yet. */
export const isChasePaid = (chase?: PublicChase | null) =>
  ["Payment-Received", "Renewed"].includes(chase?.status as string);

/**
 * What the page says once there's nothing left for the customer to do. "Renewed" is
 * further along than "Payment-Received" — the policy is actually on cover, so it must
 * not read as a fresh receipt.
 */
export const paidMessage = (chase: PublicChase) => {
  const thanks = `Thank you ${policyHolder(chase)} — your`;
  const docs = "your documents are on their way by email.";

  if (chase.status === "Renewed")
    return {
      title: "Policy renewed",
      text: `${thanks} policy ${chase.policyReferenceNumber} has been renewed and ${docs}`,
    };

  return chase.paymentType === "cash"
    ? {
        title: "Payment received",
        text: `${thanks} renewal for ${chase.policyReferenceNumber} is paid and ${docs}`,
      }
    : {
        title: "Renewal confirmed",
        text: `${thanks} renewal for ${chase.policyReferenceNumber} is confirmed and will be collected on your existing plan — ${docs}`,
      };
};

export const money = (value?: number | null) =>
  `£${Number(value || 0).toFixed(2)}`;

export const policyHolder = (chase: PublicChase) =>
  chase.companyName || `${chase.forename} ${chase.surname}`;

/** The customer bought from a brand, not from Rensure — the page wears theirs. */
export const brandOf = (chase: PublicChase) =>
  BRANDS[chase.brandCode as BrandKey] ?? BRANDS.BUSINESS_COMPARE_DIRECT;

/** Templates point the logo at the email host; this app serves its own copy. */
export const brandLogo = (chase: PublicChase) =>
  brandOf(chase).logoUrl?.replace(/^.*\/logos\//, "/logos/") ?? "";

/** Total the customer pays today — the active quote, falling back to the chase premium. */
export const cashTotal = (chase: PublicChase) =>
  Number(chase.quote?.premium ?? chase.premium ?? 0);

export const policyRows = (chase: PublicChase): Row[] =>
  [
    // The reference is already in the brand header — don't say it twice.
    { label: "Policyholder", value: policyHolder(chase) },
    { label: "Cover", value: chase.coverType },
    { label: "Insurer", value: chase.quote?.insurer || chase.insurer },
    {
      label: "Renewal date",
      value:
        chase.renewalDate && moment(chase.renewalDate).format("DD/MM/YYYY"),
    },
  ].filter((row) => row.value) as Row[];

/**
 * Quote breakdown — only the line items the quote actually carries.
 *
 * IPT sits directly under the premium it is charged on, because that is the only
 * line it applies to: the fees carry none and the discount comes off the taxed
 * total. Same order as `quoteTotal`, so the rows add up to what's owed.
 */
export const quoteRows = (chase: PublicChase): Row[] => {
  const quote = chase.quote;
  if (!quote) return [];

  return [
    { label: "Insurer premium", value: quote.insurerPremium },
    {
      label: `Insurance Premium Tax (${IPT_RATE * 100}%)`,
      value: r2(Number(quote.insurerPremium ?? 0) * IPT_RATE),
      sub: true,
    },
    { label: "Broker fee", value: quote.brokerFee },
    { label: "Insurer fee", value: quote.insurerFee },
    { label: "Discount", value: quote.discount, credit: true },
  ]
    .filter((row) => Number(row.value))
    .map((row) => ({
      ...row,
      value: (row.credit ? "− " : "") + money(row.value),
    }));
};

/**
 * The plan charged on top of the quote — reads top to bottom as the sum it is:
 * the price the breakdown adds up to, the interest applied to it, what that
 * comes to, and over how many months. The monthly figure is the section total.
 */
export const instalmentRows = (chase: PublicChase): Row[] =>
  [
    {
      label: "Policy price",
      value: money(chase.quote?.premium ?? chase.premium),
    },
    chase.interestRate
      ? {
          label: "Interest rate",
          value: `${Number(chase.interestRate).toFixed(2)}%`,
          sub: true,
        }
      : null,
    {
      label: "Total payable",
      value: money(chase.totalPayable ?? chase.premium),
    },
    chase.instalments
      ? { label: "Number of instalments", value: `${chase.instalments}` }
      : null,
  ].filter(Boolean) as Row[];

/** Must all be ticked before Pay Now is allowed. */
export const PAYMENT_TERMS = [
  {
    key: "isCardGuarantue",
    label:
      "I agree to my credit/debit card being used as a card guarantee for this policy.",
  },
  {
    key: "termsConditions",
    label:
      "I have read and accept the Terms & Conditions and the policy documents.",
  },
];
